import { HttpRequest } from '@angular/common/http';
import { FormGroup } from '@angular/forms';
import {
  Cart,
  CartItem,
  FullDetailedPaymentInfo,
  Order,
  OrderItem,
  Raffle,
  SessionModelResponse,
  User
} from '@core/models';

export class BillingStarted {
  cart_total: number;
  items: BillingStartedItem[];
  quantity_total: number;

  create(cart: Cart): BillingStarted {
    const billingStarted = new BillingStarted();
    billingStarted.items = [];
    billingStarted.cart_total = cart.cached_amount;
    cart.items.forEach((item: CartItem) => {
      billingStarted.items.push(new BillingStartedItem().create(item));
    });
    billingStarted.quantity_total = cart.cached_item_count;

    return billingStarted;
  }
}

export class BillingStartedItem {
  raffle_id: string;
  raffle_name: string;
  quantity: number;

  create(item: CartItem): BillingStartedItem {
    const billingStartedItem = new BillingStartedItem();
    billingStartedItem.raffle_id = item.type_id1;
    billingStartedItem.raffle_name = item.name;
    billingStartedItem.quantity = item.quantity;

    return billingStartedItem;
  }
}

export class BillingStepCompleted {
  cart_total: number;

  create(cart: Cart): BillingStepCompleted {
    const billingStepCompleted = new BillingStepCompleted();
    billingStepCompleted.cart_total = cart.cached_amount;

    return billingStepCompleted;
  }
}

export class CartViewed {
  cart_total: number;
  cart_items_count: number;

  create(cart: Cart): CartViewed {
    const cartViewed = new CartViewed();
    cartViewed.cart_total = 0;
    cartViewed.cart_items_count = 0;

    if (cart) {
      cartViewed.cart_total = cart.cached_amount;
      cartViewed.cart_items_count = cart.items.length;
    }

    return cartViewed;
  }
}

export class CouponApplied {
  coupon_code: string;
  coupon_amount: number;

  create(couponCode: string, cart: Cart): CouponApplied {
    const couponApplied = new CouponApplied();
    couponApplied.coupon_code = couponCode;
    couponApplied.coupon_amount = Number(
      cart.coupons.find(coupon => coupon.code === couponCode).reduction
    );

    return couponApplied;
  }
}

export class ErrorTracked {
  error_type: string;
  error_message: string;
  error_code: number;
  endpoint: string;
  user_id?: string;

  create(error: any, req: HttpRequest<any>, user?: User): ErrorTracked {
    const errorTracked = new ErrorTracked();
    errorTracked.error_code = Number(error.status);
    errorTracked.error_message = error.error?.string ?? error.statusText;
    errorTracked.endpoint = error.error?.url ?? req.url;
    errorTracked.user_id = String(user?.id);
    errorTracked.error_type = errorTracked.error_code >= 500 ? 'system' : 'api';

    return errorTracked;
  }
}

export class FormError {
  form_name: string;
  errors: Record<string, string>;

  create(form: FormGroup, name: string): FormError {
    const formError = new FormError();

    const errorsList = {};
    Object.keys(form.controls).forEach(key => {
      const control = form.get(key);
      if (control.touched && !control.valid) {
        let message = 'Required';
        if (key == 'email') {
          message = 'Please provide a valid email address';
        }
        errorsList[key] = message;
      }
    });

    formError.form_name = name;
    formError.errors = errorsList;

    return formError;
  }
}

export class Login {
  user_id: string;

  create(session: SessionModelResponse): Login {
    const login = new Login();
    login.user_id = String(session.user.id);

    return login;
  }
}

export class Logout {
  user_id: string;

  create(user: User): Logout {
    const logout = new Logout();
    logout.user_id = String(user.id);

    return logout;
  }
}

export class OrderCompleted {
  // Checkout ID
  //checkout_id: string;
  // Order/transaction ID
  order_id: string;
  // Store or affiliation from which this transaction occurred
  //affiliation: string;
  // Order total after discounts but before taxes and shipping
  subtotal: number;
  // Subtotal ($) with shipping and taxes added in.
  total: number;
  // Revenue ($) associated with the transaction (including discounts, but excluding shipping and taxes)
  revenue: number;
  // Shipping cost associated with the transaction
  shipping: number;
  // Total tax associated with the transaction
  tax: number;
  // Total discount associated with the transaction
  discount: number;
  // Transaction coupon redeemed with the transaction
  coupon: string;
  // Currency code associated with the transaction
  currency: string;
  // Products in the order
  products: OrderProduct[];

  create(
    order: Order,
    products: OrderProduct[],
    total: number
  ): OrderCompleted {
    const orderCompleted = new OrderCompleted();

    orderCompleted.order_id = order.id;
    orderCompleted.subtotal = Number(order.paid_amount);
    orderCompleted.total = total;
    orderCompleted.discount = total - orderCompleted.subtotal;
    if (order.coupons && order.coupons.length > 0) {
      orderCompleted.coupon = order.coupons.map(item => item.code).join(',');
    }
    orderCompleted.tax = 0;
    orderCompleted.shipping = 0;
    orderCompleted.currency = order.currency;
    orderCompleted.products = products;

    return orderCompleted;
  }
}

export class OrderProduct {
  // Database ID of the product
  product_id: string;
  // SKU (Stock Keeping Unit) of the product
  sku: string;
  // Product category
  category: string;
  // Name of the product
  name: string;
  // Brand associated with the product
  //brand: string;
  // Variant of the product
  //variant: string;
  // Price ($) of the product
  price: number;
  // Quantity of a product
  quantity: number;
  // Coupon code for the product
  //coupon: string;
  // Position in the product list (ex. 3)
  position: number;
  // URL of the product page
  url: string;
  // Image URL of the product
  image_url: string;

  create(
    item: OrderItem,
    position: number,
    product_page_path: string
  ): OrderProduct {
    const product = new OrderProduct();

    product.product_id = item.type_id1;
    product.sku = item.item_data.slug;
    product.category = item.item_data.category ?? '';
    product.name = item.name;
    product.price = Number(item.single_price);
    product.quantity = item.quantity;
    product.position = position;
    product.url = `${window.location.protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}/${product_page_path}/${item.item_data.slug}`;
    product.image_url = item.thumbnail_url;

    return product;
  }
}

export class OrderUserSubscription {
  order_id: string;
  subscription_id: string;
  subscription_title: string;
  user_subscription_id: string;
  quantity: number;
  sub_total: number;
  total: number;

  create(item: OrderItem, order_id: string): OrderUserSubscription {
    const orderUserSubscription = new OrderUserSubscription();

    orderUserSubscription.order_id = order_id;
    orderUserSubscription.subscription_id = String(
      item.user_subscription.subscription.id
    );
    orderUserSubscription.subscription_title =
      item.user_subscription.subscription.title;
    orderUserSubscription.user_subscription_id = String(
      item.user_subscription.id
    );
    orderUserSubscription.quantity = item.user_subscription.ticket_quantity;
    orderUserSubscription.sub_total = Number(item.total_price_base);
    orderUserSubscription.total = Number(item.total_price);

    return orderUserSubscription;
  }
}

export class PaymentFailed {
  processor_reason: string;
  cart_total: number;
  payment_gateway: string;
  order_id: string;

  create(data: FullDetailedPaymentInfo): PaymentFailed {
    const paymentFailed = new PaymentFailed();
    paymentFailed.order_id = String(data.order.id);
    paymentFailed.processor_reason = data.payment.processor_reason;
    paymentFailed.payment_gateway = data.payment.processor;
    paymentFailed.cart_total = Number(data.order.paid_amount);

    return paymentFailed;
  }
}

export class ProductAdded {
  raffle_id: string;
  raffle_name: string;
  quantity: number;
  product_total: number;

  create(
    raffle_id: string,
    raffle_name: string,
    quantity: number,
    product_total: number
  ): ProductAdded {
    const productAdded = new ProductAdded();
    productAdded.raffle_id = raffle_id;
    productAdded.raffle_name = raffle_name;
    productAdded.quantity = quantity;
    productAdded.product_total = product_total;

    return productAdded;
  }
}

export class ProductRemoved {
  raffle_id: string;
  raffle_name: string;
  quantity: number;
  product_total: number;

  create(item: CartItem): ProductRemoved {
    const productRemoved = new ProductRemoved();

    productRemoved.raffle_id = item.type_id1;
    productRemoved.raffle_name = item.name;
    productRemoved.quantity = item.quantity;
    productRemoved.product_total = item.total_price;

    return productRemoved;
  }
}

export class ProductViewed {
  raffle_id: string;
  raffle_name: string;
  price: number;
  raffle_internal_category: string;

  create(raffle: Raffle, price: number): ProductViewed {
    const productViewed = new ProductViewed();
    productViewed.raffle_id = String(raffle.id);
    productViewed.raffle_name = raffle.title;
    productViewed.price = price;
    productViewed.raffle_internal_category =
      raffle.raffle_internal_category?.name;

    return productViewed;
  }
}

export class ProductViewedEarlyBirdOffer {
  raffle_id: string;
  raffle_name: string;
  bonus_draw_title: string;
  bonus_draw_prize_name: string;

  create(raffle: Raffle): ProductViewedEarlyBirdOffer {
    const productViewedEarlyBirdOffer = new ProductViewedEarlyBirdOffer();
    productViewedEarlyBirdOffer.raffle_id = String(raffle.id);
    productViewedEarlyBirdOffer.raffle_name = raffle.title;
    productViewedEarlyBirdOffer.bonus_draw_title = raffle.early_bird_info.title;
    productViewedEarlyBirdOffer.bonus_draw_prize_name =
      raffle.early_bird_info.prize_name;

    return productViewedEarlyBirdOffer;
  }
}

export class Register {
  user_id: string;

  createRegister(session: SessionModelResponse): Register {
    const register = new Register();
    register.user_id = String(session.user.id);

    return register;
  }
}

export interface SubscriptionToggleClick {
  subscription_id: string;
  state_after_click: boolean;
}

export enum PosthogEvent {
  BillingStarted = 'Billing Started',
  BillingStepCompleted = 'Billing Step Completed',
  CartViewed = 'Cart Viewed',
  CouponApplied = 'Coupon Applied',
  ErrorTracked = 'Error Tracked',
  FormError = 'Form Error',
  Login = 'Login',
  Logout = 'Logout',
  NotFound404 = '404 Error',
  OrderCompleted = 'Order Completed',
  OrderConfirmationInstantWinOfferClicked = 'Order Confirmation Instant Win Offer Clicked',
  OrderConfirmationInstantWinOfferDisplayed = 'Order Confirmation Instant Win Offer Displayed',
  PaymentFailed = 'Payment Failed',
  ProductAdded = 'Product Added',
  ProductRemoved = 'Product Removed',
  ProductViewed = 'Product Viewed',
  ProductViewedEarlyBirdOffer = 'Product Viewed Early Bird Offer',
  Register = 'Register',
  Subscribed = 'Subscribed',
  SubscriptionToggleClick = 'Subscription Toggle Click'
}
